import { AbstractNode } from '../../lang/abstracts/AbstractNode.js';
import { _eq } from '../../lang/abstracts/util.js';
import { registry } from '../../lang/registry.js';

const TBL_PLACEHOLDER = Symbol.for('tbl_placeholder');
const GROUPING_META = Symbol.for('grouping_meta');
const WINDOW_META = Symbol.for('window_meta');
const hasExplicitTimezone = (value) => typeof value === 'string'
    && /(?:z|[+-]\d{2}(?::?\d{2})?)$/i.test(value.trim());

export class ExprEngine {

    #derivedQueryCallback;
    #options;

    constructor(derivedQueryCallback = null, options = {}) {
        this.#derivedQueryCallback = derivedQueryCallback;
        this.#options = options;
    }

    async evaluate(node, compositeRow, queryCtx = {}) {
        if (!node) throw new Error(`ExprEngine: Cannot evaluate null/undefined node`);
        // Evaluate derived queries via callback
        if (['DERIVED_QUERY', 'SCALAR_SUBQUERY'].includes(node.NODE_NAME)) {
            if (!this.#derivedQueryCallback) {
                throw new Error(`ExprEngine: Node ${node.NODE_NAME} not supported in this context`);
            }
            return await this.#derivedQueryCallback(node, compositeRow, queryCtx);
        }
        // Dispatch to handler
        const handler = this[node.NODE_NAME];
        if (!handler) throw new Error(`ExprEngine: Unsupported AST node: ${node.NODE_NAME}`);
        return await handler.call(this, node, compositeRow, queryCtx);
    }

    async evaluateToScalar(expr, compositeRow, queryCtx) {
        if (expr instanceof registry.DerivedQuery) {
            const rows = [];
            for await (const row of await this.evaluate(expr, compositeRow, queryCtx)) rows.push(row);
            if (!rows.length) return;
            if (rows.length > 1) throw new Error(`[${node}] Subquery returned more than one row`);
            const values = Object.values(rows[0]);
            if (values.length > 1) throw new Error(`[${node}] Subquery returned more than one column`);
            return values[0] ?? null;
        }
        if (expr instanceof registry.RowConstructor) {
            if (expr.length !== 1) throw new Error(`Expects a scalar expression but got ${expr}`);
            return await this.evaluateToScalar(expr.entries()[0], compositeRow, queryCtx);
        }
        return await this.evaluate(expr, compositeRow, queryCtx);
    }

    async evaluateToList(expr, compositeRow, queryCtx) {
        if (expr instanceof registry.DerivedQuery) {
            const rows = [];
            for await (const row of await this.evaluate(expr, compositeRow, queryCtx)) rows.push(row);
            if (!rows.length) return [];
            if (Object.values(rows[0]).length > 1) throw new Error(`[${node}] Subquery returned more than one column`);
            return rows.map((r) => Object.values(r)[0]);
        }
        if (expr instanceof registry.RowConstructor) {
            const entries = await Promise.all(expr.entries().map((e) => this.evaluateToScalar(e, compositeRow, queryCtx)));
            // ----------- asPartial
            if (entries.some((x) => x instanceof AbstractNode)) {
                return registry.RowConstructor.fromJSON(
                    { nodeName: registry.RowConstructor.NODE_NAME, entries: entries.map(toNode) },
                    { assert: true, dialect: queryCtx.options?.dialect }
                );
            }
            // -----------
            return entries;
        }
        const result = await this.evaluate(expr, compositeRow, queryCtx);
        if (!Array.isArray(result) && !(result instanceof AbstractNode)) throw new Error(`[${expr}] Not a list`);
        return result;
    }

    // --- CLAUSES & CONSTRUCTS ---

    async SELECT_ITEM(node, compositeRow, queryCtx = {}) {
        const alias = node.alias()?.value() || (this.#options.dialect === 'mysql' ? '?column?'/* TODO */ : '?column?');
        const value = await this.evaluateToScalar(node.expr(), compositeRow, queryCtx);
        return { alias, value };
    }

    async ON_CLAUSE(node, compositeRow, queryCtx = {}) {
        const result = await this.evaluate(node.expr(), compositeRow, queryCtx);
        return result;
    }

    async USING_CLAUSE(node, compositeRow) {
        const cols = node.columns() || [node.column()];
        return cols.every((col) => {
            const colName = col.value();
            const caliases = Object.keys(compositeRow).filter((a) => a && colName in compositeRow[a]);
            if (caliases.length < 2) throw new Error(`USING clause column ${colName} not found in both tables`);
            return caliases.reduce((v, a) => v && _eq(compositeRow[a][colName], compositeRow[caliases[0]][colName]), true);
        });
    }

    // --- EXPRESSIONS ---

    async ROW_CONSTRUCTOR(node, compositeRow, queryCtx = {}) {
        const entries = await Promise.all(node.entries().map((e) => this.evaluateToScalar(e, compositeRow, queryCtx)));
        if (entries.length === 1) return entries[0];
        // ----------- asPartial
        if (entries.some((x) => x instanceof AbstractNode)) {
            return registry.RowConstructor.fromJSON(
                { nodeName: registry.RowConstructor.NODE_NAME, entries: entries.map(toNode) },
                { assert: true, dialect: queryCtx.options?.dialect }
            );
        }
        // -----------
        return entries;
    }

    async TYPED_ROW_CONSTRUCTOR(node, compositeRow, queryCtx = {}) {
        return await this.ROW_CONSTRUCTOR(node, compositeRow, queryCtx);
    }

    async PG_TYPED_ARRAY_LITERAL(node, compositeRow, queryCtx = {}) {
        const entries = await Promise.all(node.entries().map((a) => this.evaluate(a, compositeRow, queryCtx)));
        // ----------- asPartial
        if (entries.some((x) => x instanceof AbstractNode)) {
            return registry.PGTypedArrayLiteral.fromJSON(
                { nodeName: registry.PGTypedArrayLiteral.NODE_NAME, entries: entries.map(toNode) },
                { assert: true, dialect: queryCtx.options?.dialect }
            );
        }
        // -----------
        return entries;
    }

    async CASE_EXPR(node, compositeRow, queryCtx = {}) {
        const subject = node.subject()
            ? await this.evaluate(node.subject(), compositeRow, queryCtx)
            : undefined;

        const partialModeBranches = [];

        for (const branch of node) {
            const condition = await this.evaluate(branch.condition(), compositeRow, queryCtx);
            // ----------- asPartial
            if (subject instanceof AbstractNode || condition instanceof AbstractNode) {
                partialModeBranches.push({
                    nodeName: registry.CaseBranch.NODE_NAME,
                    condition: toNode(condition),
                    consequent: toNode(await this.evaluate(branch.consequent(), compositeRow, queryCtx)),
                });
                continue;
            }
            // -----------
            const test = subject === undefined ? !!condition : _eq(subject, condition);
            if (test) {
                const result = await this.evaluate(branch.consequent(), compositeRow, queryCtx);
                if (!partialModeBranches.length) return result; // Whether or not resolved
                // ----------- asPartial
                partialModeBranches.push({
                    nodeName: registry.CaseBranch.NODE_NAME,
                    condition: toNode(condition),
                    consequent: toNode(result),
                });
                continue;
            }
        }

        let alternate;
        if (node.alternate()) {
            alternate = await this.evaluate(node.alternate(), compositeRow, queryCtx);
            if (!partialModeBranches.length) return alternate; // Whether or not resolved
        }
        // ----------- asPartial
        if (partialModeBranches.length) {
            return registry.CaseExpr.fromJSON(
                { nodeName: registry.CaseExpr.NODE_NAME, subject: toNode(subject), entries: partialModeBranches, alternate: toNode(alternate) },
                { assert: true, dialect: queryCtx.options?.dialect }
            );
        }
        // -----------
        return null;
    }

    async CAST_EXPR(node, compositeRow, queryCtx = {}) {
        return await this._CAST_EXPR(node.expr(), node.dataType(), compositeRow, queryCtx);
    }

    async PG_CAST_EXPR2(node, compositeRow, queryCtx = {}) {
        return await this._CAST_EXPR(node.left(), node.right(), compositeRow, queryCtx);
    }

    async _CAST_EXPR(expr, dataType, compositeRow, queryCtx = {}) {
        const L = await this.evaluateToScalar(expr, compositeRow, queryCtx);
        // ----------- asPartial
        if (L instanceof AbstractNode) {
            return registry.CastExpr.fromJSON(
                { nodeName: registry.CastExpr.NODE_NAME, expr: toNode(L), data_type: dataType.jsonfy() },
                { assert: true, dialect: queryCtx.options?.dialect }
            );
        }
        // -----------
        if (L === null || L === undefined) return null;
        const DT = dataType.value();
        const asString = (value) => String(value);
        const asNumber = (value) => Number(value);
        const asIsoTimestamp = (date) => date.toISOString().slice(0, 19).replace('T', ' ');
        const asIsoTime = (date) => date.toISOString().slice(11, 19);
        const normalizeBigInt = (value) => {
            if (typeof value === 'bigint') return value;
            if (typeof value === 'number') return BigInt(Math.trunc(value));
            if (typeof value === 'string') return BigInt(value.trim());
            return BigInt(value);
        };
        const normalizeJson = (value) => {
            if (typeof value !== 'string') return value;
            return JSON.parse(value);
        };
        const normalizeBytea = (value) => {
            if (value instanceof Uint8Array) return value;
            if (typeof Buffer !== 'undefined' && value instanceof Buffer) return value;
            if (value instanceof ArrayBuffer) return new Uint8Array(value);
            if (Array.isArray(value)) return Uint8Array.from(value);
            if (typeof value === 'string') return new TextEncoder().encode(value);
            return value;
        };
        const normalizeDate = (value) => {
            if (value instanceof Date) return value.toISOString().slice(0, 10);
            const str = String(value).trim();
            if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
            return new Date(str).toISOString().slice(0, 10);
        };
        const normalizeTime = (value) => {
            if (value instanceof Date) return asIsoTime(value);
            const str = String(value).trim();
            const match = str.match(/\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?/);
            if (match) return match[0];
            return asIsoTime(new Date(str));
        };
        const normalizeTimestamp = (value) => {
            if (value instanceof Date) return asIsoTimestamp(value);
            const str = String(value).trim();
            if (/^\d{4}-\d{2}-\d{2}[ t]\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?$/.test(str)) {
                return str.replace('T', ' ');
            }
            return asIsoTimestamp(new Date(str));
        };
        const normalizeTimestamptz = (value) => {
            if (value instanceof Date) return value.toISOString();
            const str = String(value).trim();
            if (/^\d{4}-\d{2}-\d{2}[ t]\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?(?:z|[+-]\d{2}(?::?\d{2})?)$/i.test(str)) {
                return str;
            }
            return new Date(str).toISOString();
        };
        switch (DT) {
            case 'SMALLINT':
            case 'INT':
            case 'INTEGER':
            case 'SERIAL':
                return asNumber(L);
            case 'NUMERIC':
            case 'DECIMAL':
            case 'REAL':
            case 'DOUBLE PRECISION':
                return asNumber(L);
            case 'BIGINT':
            case 'BIGSERIAL':
                return normalizeBigInt(L);
            case 'TEXT':
            case 'VARCHAR':
            case 'CHAR':
            case 'UUID':
            case 'INTERVAL':
                return asString(L);
            case 'JSON':
            case 'JSONB':
                return normalizeJson(L);
            case 'ARRAY':
                return typeof L === 'string' ? JSON.parse(L) : L;
            case 'BYTEA':
                return normalizeBytea(L);
            case 'DATE':
                return normalizeDate(L);
            case 'TIME':
                return normalizeTime(L);
            case 'TIMESTAMP':
                return normalizeTimestamp(L);
            case 'TIMESTAMPTZ':
                return normalizeTimestamptz(L);
            case 'BOOLEAN':
                if (typeof L === 'boolean') return L;
                if (typeof L === 'number') return L !== 0;
                if (typeof L === 'bigint') return L !== 0n;
                if (typeof L === 'string') {
                    const normalized = L.trim().toLowerCase();
                    if (['true', 't', '1', 'yes', 'y', 'on'].includes(normalized)) return true;
                    if (['false', 'f', '0', 'no', 'n', 'off'].includes(normalized)) return false;
                }
                return Boolean(L);
            default: return L;
        }
    }

    async PREDICATE_EXPR(node, compositeRow, queryCtx = {}) {
        const predicate = node.predicate();
        switch (predicate) {
            case 'EXISTS':
                const expr = await this.evaluate(node.expr(), compositeRow, queryCtx);
                // ----------- asPartial
                if (expr instanceof AbstractNode) {
                    return registry.PredicateExpr.fromJSON(
                        { nodeName: registry.PredicateExpr.NODE_NAME, predicate, expr },
                        { assert: true, dialect: queryCtx.options?.dialect }
                    );
                }
                // -----------
                const result = (await expr.next()).value;
                return !!result;
            default: throw new Error(`ExprEngine: Unimplemented predicate ${node.predicate()}`);
        }
    }

    async IN_EXPR(node, compositeRow, queryCtx = {}) {
        const L = await this.evaluateToScalar(node.left(), compositeRow, queryCtx);
        const R = await this.evaluateToList(node.right(), compositeRow, queryCtx);
        const negation = node.negation();
        // ----------- asPartial
        if (L instanceof AbstractNode || R instanceof AbstractNode) {
            return registry.InExpr.fromJSON(
                { nodeName: registry.InExpr.NODE_NAME, left: toNode(L), negation, operator: 'IN', right: toNode(R) },
                { assert: true, dialect: queryCtx.options?.dialect }
            );
        }
        // -----------
        const res = (val) => negation ? !val : val;
        return res(R.some((v) => _eq(L, v)));
    }

    async BETWEEN_EXPR(node, compositeRow, queryCtx = {}) {
        const L = await this.evaluateToScalar(node.left(), compositeRow, queryCtx);
        const R = await Promise.all(node.right().map((e) => this.evaluateToScalar(e, compositeRow, queryCtx)));
        const negation = node.negation();
        // ----------- asPartial
        if (L instanceof AbstractNode || R.some((x) => x instanceof AbstractNode)) {
            return registry.BetweenExpr.fromJSON(
                { nodeName: registry.BetweenExpr.NODE_NAME, left: toNode(L), negation, operator: 'BETWEEN', right: R.map(toNode) },
                { assert: true, dialect: queryCtx.options?.dialect }
            );
        }
        // -----------
        const res = (val) => negation ? !val : val;
        return res(L >= R[0] && L <= R[1]);
    }

    async DISTINCT_FROM_EXPR(node, compositeRow, queryCtx = {}) {
        const L = await this.evaluate(node.left(), compositeRow, queryCtx);
        const R = await this.evaluate(node.right(), compositeRow, queryCtx);
        const logic = node.logic();
        // ----------- asPartial
        if (L instanceof AbstractNode || R instanceof AbstractNode) {
            return registry.DistinctFromExpr.fromJSON(
                { nodeName: registry.DistinctFromExpr.NODE_NAME, left: toNode(L), logic, operator: 'DISTINCT FROM', right: toNode(R) },
                { assert: true, dialect: queryCtx.options?.dialect },
            );
        }
        // -----------
        const negation = logic === 'IS NOT';
        const res = (val) => negation ? !val : val;
        return res(!_eq(L, R));
    }

    async BINARY_EXPR(node, compositeRow, queryCtx = {}) {
        const op = node.operator().toUpperCase();
        const negation = node.negation();

        const asPartial = (L, R) => {
            return registry.BinaryExpr.fromJSON(
                { nodeName: registry.BinaryExpr.NODE_NAME, operator: op, left: toNode(L), right: toNode(R) },
                { assert: true, dialect: queryCtx.options?.dialect }
            );
        };

        const res = (val) => negation ? !val : val;

        const compare = (L, R) => {
            const anyIsNull = L === null || R === null;
            if (anyIsNull && op !== 'IS' && op !== 'IS NOT') return false;
            switch (op) {
                case '=':
                case 'IS': return _eq(L, R);
                case '<>':
                case '!=':
                case 'IS NOT': return !_eq(L, R);
                case '<': return L < R;
                case '<=': return L <= R;
                case '>': return L > R;
                case '>=': return L >= R;
                case 'LIKE': return likeCompare(String(L), String(R));
                default: throw new Error(`ExprEngine: Unsupported comparison operator ${op}`);
            }
        };

        if (node.right() instanceof registry.QuantitativeExpr) {
            const quantifier = node.right().quantifier();
            const L = await this.evaluateToScalar(node.left(), compositeRow, queryCtx);
            const R = await this.evaluateToList(node.right().expr(), compositeRow, queryCtx);
            // ----------- asPartial
            if (L instanceof AbstractNode || R instanceof AbstractNode) {
                return asPartial(L, registry.QuantitativeExpr.fromJSON(
                    { nodeName: registry.QuantitativeExpr.NODE_NAME, quantifier, expr: toNode(R) },
                    { assert: true, dialect: queryCtx.options?.dialect }
                ));
            }
            // -----------
            switch (quantifier) {
                case 'ALL': return res(R.every((R) => compare(L, R)));
                case 'ANY':
                case 'SOME': return res(R.some((R) => compare(L, R)));
            }
        }

        const L = await this.evaluateToScalar(node.left(), compositeRow, queryCtx);
        const R = await this.evaluateToScalar(node.right(), compositeRow, queryCtx);

        // Logical - before asPartial
        if (op === 'AND') return res(L && R);
        if (op === 'OR') return res(L || R);

        // ----------- asPartial
        if (L instanceof AbstractNode || R instanceof AbstractNode) {
            return asPartial(L, R);
        }
        // -----------
        switch (op) {
            // Comparison
            case '=':
            case 'IS':
            case '<>':
            case '!=':
            case 'IS NOT':
            case '<':
            case '<=':
            case '>':
            case '>=':
            case 'LIKE': return res(compare(L, R));

            // Arithmetic
            case '+': return Number(L) + Number(R);
            case '-': return Number(L) - Number(R);
            case '/': return Number(L) / Number(R);
            case '*': return Number(L) * Number(R);
            case '%': return Number(L) % Number(R);

            // String
            case '||': return String(L ?? '') + String(R ?? '');

            // JSON
            case '->':
            case '->>': {
                // JSON path accessors
                if (L == null) return null;
                let val;
                if (typeof R === 'number') {
                    val = Array.isArray(L) ? L[R] : undefined;
                } else if (typeof R === 'string') {
                    if (typeof L === 'object' && !Array.isArray(L)) val = L[R];
                    else if (Array.isArray(L) && !isNaN(R)) val = L[Number(R)];
                }
                return op === '->' ? val : (val == null ? null : String(val));
            }
            case '#>':
            case '#>>': {
                // JSON path extraction (Postgres)
                // L #> R, where R is array of keys
                if (L == null || !Array.isArray(R)) return null;
                let val = L;
                for (const key of R) {
                    if (val == null) return null;
                    if (Array.isArray(val) && !isNaN(key)) val = val[Number(key)];
                    else if (typeof val === 'object') val = val[key];
                    else return null;
                }
                return op === '#>' ? val : (val == null ? null : String(val));
            }
            case '@>': {
                // JSON contains (Postgres)
                if (L == null || R == null) return false;
                if (typeof L !== 'object' || typeof R !== 'object') return false;
                // Simple deep contains check
                const contains = (a, b) => {
                    if (typeof b !== 'object' || b == null) return a === b;
                    if (Array.isArray(b)) {
                        if (!Array.isArray(a)) return false;
                        return b.every((v, i) => contains(a[i], v));
                    }
                    return Object.keys(b).every((k) => contains(a[k], b[k]));
                };
                return contains(L, R);
            }
            case '<@': {
                // JSON is contained by (Postgres)
                if (L == null || R == null) return false;
                if (typeof L !== 'object' || typeof R !== 'object') return false;
                // Simple deep contains check
                const contains = (a, b) => {
                    if (typeof b !== 'object' || b == null) return a === b;
                    if (Array.isArray(b)) {
                        if (!Array.isArray(a)) return false;
                        return b.every((v, i) => contains(a[i], v));
                    }
                    return Object.keys(b).every((k) => contains(a[k], b[k]));
                };
                return contains(R, L);
            }
            case '?': {
                // JSON key exists (Postgres)
                if (L == null || typeof L !== 'object') return false;
                if (Array.isArray(L)) return L.includes(R);
                return Object.prototype.hasOwnProperty.call(L, R);
            }
            case '?|': {
                // JSON key exists any (Postgres)
                if (L == null || typeof L !== 'object' || !Array.isArray(R)) return false;
                if (Array.isArray(L)) return R.some((key) => L.includes(key));
                return R.some((key) => Object.prototype.hasOwnProperty.call(L, key));
            }
            case '?&': {
                // JSON key exists all (Postgres)
                if (L == null || typeof L !== 'object' || !Array.isArray(R)) return false;
                if (Array.isArray(L)) return R.every((key) => L.includes(key));
                return R.every((key) => Object.prototype.hasOwnProperty.call(L, key));
            }

            default: throw new Error(`ExprEngine: Unsupported binary operator ${op}`);
        }
    }

    async UNARY_EXPR(node, compositeRow, queryCtx = {}) {
        const op = node.operator().toUpperCase();
        const v = await this.evaluateToScalar(node.operand(), compositeRow, queryCtx);
        // ----------- asPartial
        if (v instanceof AbstractNode) {
            return registry.UnaryExpr.fromJSON(
                { nodeName: registry.UnaryExpr.NODE_NAME, operand: v },
                { assert: true, dialect: queryCtx.options?.dialect }
            );
        }
        // -----------
        switch (op) {
            case 'NOT': return !Boolean(v);
            case '-': return -v;
            default: throw new Error(`ExprEngine: Unsupported unary operator ${op}`);
        }
    }

    async CALL_EXPR(node, compositeRow, queryCtx = {}) {
        const fn = node.name().toUpperCase();

        // SRFs

        if (fn === 'UNNEST' || fn === 'GENERATE_SERIES') {
            const args = await Promise.all(node.arguments().map((a) => this.evaluate(a, compositeRow, queryCtx)));

            return (function* () {
                if (fn === 'UNNEST') {
                    for (let i = 0; i < args[0].length; i++) yield args.map((arr) => arr[i] ?? null);
                }

                if (fn === 'GENERATE_SERIES') {
                    for (let x = args[0]; x <= args[1]; x += args[2] ?? 1) yield [x];
                }
            })();
        }

        // Meta functions

        if (fn === 'GROUPING' || fn === 'GROUPING_ID') {
            const meta = compositeRow[GROUPING_META];
            if (!meta) throw new Error(`${fn}() called outside of grouping pipeline`);

            const args = node.arguments();

            const getBit = (arg) => {
                const bitIndex = meta.exprIndex.get(arg);
                if (bitIndex !== undefined) return (meta.groupingId >> bitIndex) & 1;

                if (!(arg instanceof registry.ColumnRef1)) {
                    throw new Error(`${fn}() argument must be a grouping column reference`);
                }
                const alias = arg.qualifier()?.value() || '';
                const colName = arg.value();
                return meta.groupingColumnsMap.get(alias)?.has(colName) ? 0 : 1;
            };

            // Fast path for GROUPING_ID when args match all top-level grouping expressions
            if (fn === 'GROUPING_ID' &&
                args.length === meta.exprIndex.size &&
                args.every((arg, i) => meta.exprIndex.has(arg) && meta.exprIndex.get(arg) === i)
            ) {
                return meta.groupingId;
            }

            // Compute mask manually
            let mask = 0;
            for (let i = 0; i < args.length; i++) {
                const bit = getBit(args[i]);
                if (fn === 'GROUPING') return bit; // early return for single-bit GROUPING
                mask = (mask << 1) | bit;
            }

            return mask;
        }

        if (fn === 'VALUES'/* MySQL */
            && compositeRow.EXCLUDED
            && typeof compositeRow.EXCLUDED === 'object') {

            const args = node.arguments();
            const fieldName = args[0].value();

            return compositeRow.EXCLUDED[fieldName];
        }

        // Classic functions

        const args = await Promise.all(node.arguments()?.map((a) => this.evaluate(a, compositeRow, queryCtx)) || []);
        // ----------- asPartial
        if (args.some((x) => x instanceof AbstractNode)) {
            return registry.CallExpr.fromJSON(
                { nodeName: registry.CallExpr.NODE_NAME, name: fn, arguments: args.map(toNode) },
                { assert: true, dialect: queryCtx.options?.dialect }
            );
        }
        // -----------
        switch (fn) {
            case 'LOWER': return String(args[0] ?? '').toLowerCase();

            case 'UPPER': return String(args[0] ?? '').toUpperCase();

            case 'CONCAT': return args.map((arg) => arg == null ? '' : String(arg)).join('');

            case 'CONCAT_WS': {
                const [separator, ...rest] = args;
                return rest
                    .filter((arg) => arg != null)
                    .map((arg) => String(arg))
                    .join(String(separator ?? ''));
            }

            case 'TRIM':
                return args[0] == null ? null : String(args[0]).trim();

            case 'LTRIM':
                return args[0] == null ? null : String(args[0]).replace(/^\s+/, '');

            case 'RTRIM':
                return args[0] == null ? null : String(args[0]).replace(/\s+$/, '');

            case 'LENGTH': return args[0] == null ? null : String(args[0]).length;

            case 'ABS': return Math.abs(Number(args[0]));

            case 'ROUND': {
                const value = Number(args[0]);
                const scale = args[1] == null ? 0 : Number(args[1]);
                const factor = 10 ** scale;
                return Math.round(value * factor) / factor;
            }

            case 'FLOOR': return Math.floor(Number(args[0]));

            case 'CEIL':
            case 'CEILING': return Math.ceil(Number(args[0]));

            case 'GREATEST':
                return args.reduce((max, cur) => max == null || cur > max ? cur : max, null);

            case 'LEAST':
                return args.reduce((min, cur) => min == null || cur < min ? cur : min, null);

            case 'COALESCE': return args.reduce((prev, cur) => prev !== null ? prev : cur, null);

            case 'NULLIF': return _eq(args[0], args[1]) ? null : args[0];

            case 'REPLACE':
                return args[0] == null ? null : String(args[0]).split(String(args[1] ?? '')).join(String(args[2] ?? ''));

            case 'SUBSTRING':
            case 'SUBSTR': {
                if (args[0] == null) return null;
                const source = String(args[0]);
                const start = Math.max(Number(args[1] ?? 1) - 1, 0);
                const length = args[2] == null ? undefined : Math.max(Number(args[2]), 0);
                return length === undefined ? source.slice(start) : source.slice(start, start + length);
            }

            case 'POSITION': {
                const needle = String(args[0] ?? '');
                const haystack = String(args[1] ?? '');
                const idx = haystack.indexOf(needle);
                return idx < 0 ? 0 : idx + 1;
            }

            case 'JSON_BUILD_ARRAY':
            case 'JSON_ARRAY': return args;

            case 'JSON_BUILD_OBJECT':
            case 'JSON_OBJECT': {
                if (args.length % 2 !== 0) throw new Error('JSON_BUILD_OBJECT requires an even number of arguments');
                const buildObj = Object.create(null);
                for (let i = 0; i < args.length; i += 2) {
                    buildObj[args[i]] = args[i + 1];
                }
                return buildObj;
            }

            
            case 'NOW': return new Date();

            case 'CURRENT_DATE': return new Date().toISOString().slice(0, 10);

            case 'CURRENT_TIME': return new Date().toISOString().slice(11, 19);

            case 'CURRENT_TIMESTAMP': return new Date().toISOString();

            case 'MAKE_DATE': {
                const [year, month, day] = args.map((arg) => Number(arg));
                return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            }

            case 'MAKE_TIME': {
                const [hour, minute, second] = args.map((arg) => Number(arg));
                return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;
            }

            case 'MAKE_TIMESTAMP': {
                const [year, month, day, hour, minute, second] = args.map((arg) => Number(arg));
                return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;
            }

            case 'EXTRACT':
                const [field, date1] = args;
                const dateObj1 = new Date(date1);
                const fieldName = field.toUpperCase();
                const useUTC1 = hasExplicitTimezone(date1);
                const getYear1 = useUTC1 ? dateObj1.getUTCFullYear() : dateObj1.getFullYear();
                const getMonth1 = useUTC1 ? dateObj1.getUTCMonth() : dateObj1.getMonth();
                const getDay1 = useUTC1 ? dateObj1.getUTCDate() : dateObj1.getDate();
                const getHour1 = useUTC1 ? dateObj1.getUTCHours() : dateObj1.getHours();
                const getMinute1 = useUTC1 ? dateObj1.getUTCMinutes() : dateObj1.getMinutes();
                const getSecond1 = useUTC1 ? dateObj1.getUTCSeconds() : dateObj1.getSeconds();
                switch (fieldName) {
                    case 'YEAR': return getYear1;
                    case 'MONTH': return getMonth1 + 1;
                    case 'DAY': return getDay1;
                    case 'HOUR': return getHour1;
                    case 'MINUTE': return getMinute1;
                    case 'SECOND': return getSecond1;
                    default: throw new Error(`Unsupported date/time field ${field}`);
                }

            case 'DATE_TRUNC':
                const [dateUnit, date2] = args;
                const dateObj2 = new Date(date2);
                const unitName = dateUnit.toUpperCase();
                const useUTC2 = hasExplicitTimezone(date2);
                const getYear2 = useUTC2 ? dateObj2.getUTCFullYear() : dateObj2.getFullYear();
                const getMonth2 = (useUTC2 ? dateObj2.getUTCMonth() : dateObj2.getMonth()) + 1;
                const getDay2 = useUTC2 ? dateObj2.getUTCDate() : dateObj2.getDate();
                const getHour2 = useUTC2 ? dateObj2.getUTCHours() : dateObj2.getHours();
                const getMinute2 = useUTC2 ? dateObj2.getUTCMinutes() : dateObj2.getMinutes();
                const getSecond2 = useUTC2 ? dateObj2.getUTCSeconds() : dateObj2.getSeconds();
                switch (unitName) {
                    case 'YEAR': return `${getYear2}-01-01`;
                    case 'MONTH': return `${getYear2}-${String(getMonth2).padStart(2, '0')}-01`;
                    case 'DAY': return `${getYear2}-${String(getMonth2).padStart(2, '0')}-${String(getDay2).padStart(2, '0')}`;
                    case 'HOUR': return `${getYear2}-${String(getMonth2).padStart(2, '0')}-${String(getDay2).padStart(2, '0')}T${String(getHour2).padStart(2, '0')}:00:00`;
                    case 'MINUTE': return `${getYear2}-${String(getMonth2).padStart(2, '0')}-${String(getDay2).padStart(2, '0')}T${String(getHour2).padStart(2, '0')}:${String(getMinute2).padStart(2, '0')}:00`;
                    case 'SECOND': return `${getYear2}-${String(getMonth2).padStart(2, '0')}-${String(getDay2).padStart(2, '0')}T${String(getHour2).padStart(2, '0')}:${String(getMinute2).padStart(2, '0')}:${String(getSecond2).padStart(2, '0')}`;
                    default: throw new Error(`Unsupported date/time unit ${dateUnit}`);
                }
            default:
                throw new Error(`ExprEngine: Unsupported function ${node.name()}`);
        }
    }

    async AGGR_CALL_EXPR(node, compositeRow, queryCtx = {}) {

        let metaData;

        if (node.overClause()) {
            const meta = compositeRow[WINDOW_META];
            if (!meta) throw new Error(`${node} called outside of window processing pipeline (1)`);
            if (!node.winHash) throw new Error(`${node} called outside of window processing pipeline (2)`);
            if (!meta[node.winHash]) throw new Error(`${node} called outside of window processing pipeline (3)`);
            metaData = meta[node.winHash];
        } else {
            const meta = compositeRow[GROUPING_META];
            if (!meta) throw new Error('GROUPING() called outside of grouping pipeline');
            metaData = meta;
        }

        const { window, frameStart, frameEnd, offset = 0 } = metaData;
        const fn = node.name().toUpperCase();
        const args = node.arguments();
        const expr = args[0] || null;

        switch (fn) {
            case 'COUNT': {
                if (!expr || expr instanceof registry.ColumnRef0) {
                    return frameEnd - frameStart + 1;
                }
                let cnt = 0;
                for (let j = frameStart; j <= frameEnd; j++) {
                    const entry = window[j];
                    const v = await this.evaluate(expr, entry, queryCtx);
                    if (v !== null && v !== undefined) cnt++;
                }
                return cnt;
            }

            case 'SUM':
            case 'AVG': {
                if (!expr) return null;
                let sum = 0, cnt = 0;
                for (let j = frameStart; j <= frameEnd; j++) {
                    const entry = window[j];
                    const v = await this.evaluate(expr, entry, queryCtx);
                    if (v !== null && !Number.isNaN(Number(v))) {
                        sum += Number(v);
                        cnt++;
                    }
                }
                if (fn === 'SUM') return cnt === 0 ? null : sum;
                return cnt === 0 ? null : sum / cnt;
            }

            case 'MIN':
            case 'MAX': {
                if (!expr) return null;
                let result = null;
                for (let j = frameStart; j <= frameEnd; j++) {
                    const entry = window[j];
                    const v = await this.evaluate(expr, entry, queryCtx);
                    if (v == null) continue;
                    if (result == null) {
                        result = v;
                    } else {
                        if (fn === 'MIN' && v < result) result = v;
                        if (fn === 'MAX' && v > result) result = v;
                    }
                }
                return result;
            }

            case 'JSON_AGG':
            case 'JSON_ARRAYAGG': {

                if (!expr) return [];
                const arr = [];

                for (let j = frameStart; j <= frameEnd; j++) {
                    const entry = window[j];
                    arr.push(await this.evaluate(expr, entry, queryCtx));
                }
                return arr;
            }

            case 'JSON_OBJECT_AGG':
            case 'JSON_OBJECTAGG': {

                const keyExpr = args[0], valExpr = args[1];
                if (!keyExpr || !valExpr) return {};
                const obj = Object.create(null);

                for (let j = frameStart; j <= frameEnd; j++) {
                    const entry = window[j];
                    const k = await this.evaluate(keyExpr, entry, queryCtx);
                    const v = await this.evaluate(valExpr, entry, queryCtx);
                    obj[k] = v;
                }
                return obj;
            }

            case 'STRING_AGG': {

                const expr = args[0];
                const delimiter = args[1] ? await this.evaluate(args[1], compositeRow, queryCtx) : ',';
                const arr = [];

                for (let j = frameStart; j <= frameEnd; j++) {
                    const v = await this.evaluate(expr, window[j], queryCtx);
                    if (v != null) arr.push(String(v));
                }
                return arr.join(delimiter);
            }

            case 'ARRAY_AGG': {
                const expr = args[0];
                const arr = [];
                for (let j = frameStart; j <= frameEnd; j++) {
                    arr.push(await this.evaluate(expr, window[j], queryCtx));
                }
                return arr;
            }

            case 'BIT_AND': {
                const expr = args[0];
                let result = ~0; // all bits set
                for (let j = frameStart; j <= frameEnd; j++) {
                    const v = await this.evaluate(expr, window[j], queryCtx);
                    if (v != null) result &= Number(v);
                }
                return result;
            }

            case 'BIT_OR': {
                const expr = args[0];
                let result = 0;
                for (let j = frameStart; j <= frameEnd; j++) {
                    const v = await this.evaluate(expr, window[j], queryCtx);
                    if (v != null) result |= Number(v);
                }
                return result;
            }

            case 'BOOL_AND': {
                const expr = args[0];
                for (let j = frameStart; j <= frameEnd; j++) {
                    const v = await this.evaluate(expr, window[j], queryCtx);
                    if (!v) return false;
                }
                return true;
            }

            case 'BOOL_OR': {
                const expr = args[0];
                for (let j = frameStart; j <= frameEnd; j++) {
                    const v = await this.evaluate(expr, window[j], queryCtx);
                    if (v) return true;
                }
                return false;
            }

            // -------- Window function proper

            case 'ROW_NUMBER':
                return offset + 1;

            case 'RANK':
            case 'PERCENT_RANK': {
                const myKeysHash = metaData.orderKeysHash;
                let rank;
                // find first peer group member
                for (let i = 0; i <= offset; i++) {
                    const otherKeysHash = window[i][WINDOW_META][node.winHash].orderKeysHash;
                    if (myKeysHash === otherKeysHash) {
                        rank = i + 1; // 1-based
                        break;
                    }
                }
                if (fn === 'RANK') return rank;
                // PERCENT_RANK
                const total = window.length;
                return total === 1 ? 0 : (rank - 1) / (total - 1);
            }

            case 'DENSE_RANK': {
                const seen = new Set();
                for (let i = 0; i <= offset; i++) {
                    const otherKeysHash = window[i][WINDOW_META][node.winHash].orderKeysHash;
                    if (!seen.has(otherKeysHash)) {
                        seen.add(otherKeysHash);
                    }
                }
                return seen.size;
            }

            case 'NTILE': {
                const buckets = Number(await this.evaluateToScalar(args[0], compositeRow, queryCtx));
                if (!Number.isInteger(buckets) || buckets <= 0) {
                    throw new Error(`[${node}] NTILE(n) requires a positive integer`);
                }
                const total = window.length;
                const baseSize = Math.floor(total / buckets);
                const remainder = total % buckets;
                // bucket boundaries
                let threshold = 0;
                for (let b = 1; b <= buckets; b++) {
                    const bucketSize = baseSize + (b <= remainder ? 1 : 0);
                    if (offset < threshold + bucketSize) {
                        return b;
                    }
                    threshold += bucketSize;
                }
            }

            case 'CUME_DIST': {
                const total = window.length;
                const myKeysHash = metaData.orderKeysHash;
                let lastIndex = offset;
                for (let i = offset + 1; i < total; i++) {
                    const otherKeysHash = window[i][WINDOW_META][node.winHash].orderKeysHash;
                    if (otherKeysHash === myKeysHash) lastIndex = i;
                    else break;
                }
                return (lastIndex + 1) / total;
            }

            case 'LAG': {
                const expr = args[0];
                const lagBy = Number(await this.evaluate(args[1] ?? { value: 1 }, compositeRow, queryCtx));
                const defaultValue = args[2] ? await this.evaluate(args[2], compositeRow, queryCtx) : null;
                const targetIndex = offset - lagBy;
                return targetIndex >= 0 ? await this.evaluate(expr, window[targetIndex], queryCtx) : defaultValue;
            }

            case 'LEAD': {
                const expr = args[0];
                const leadBy = Number(await this.evaluate(args[1] ?? { value: 1 }, compositeRow, queryCtx));
                const defaultValue = args[2] ? await this.evaluate(args[2], compositeRow, queryCtx) : null;
                const targetIndex = offset + leadBy;
                return targetIndex < window.length ? await this.evaluate(expr, window[targetIndex], queryCtx) : defaultValue;
            }

            case 'FIRST_VALUE': {
                const expr = args[0];
                return await this.evaluate(expr, window[frameStart], queryCtx);
            }

            case 'LAST_VALUE': {
                const expr = args[0];
                return await this.evaluate(expr, window[frameEnd], queryCtx);
            }

            case 'NTH_VALUE': {
                const expr = args[0];
                const n = Number(await this.evaluateToScalar(args[1], compositeRow, queryCtx));
                if (!Number.isInteger(n) || n <= 0) {
                    throw new Error(`[${node}] NTH_VALUE(n) requires a positive integer`);
                }
                const targetIndex = frameStart + (n - 1);
                if (targetIndex > frameEnd) return null;
                return await this.evaluate(expr, window[targetIndex], queryCtx);
            }

            default: throw new Error(`ExprEngine: Unsupported window function ${node.name()}`);
        }
    }

    async COLUMN_REF1(node, compositeRow) {
        if (!node) return undefined;
        const colName = node.value();
        const qualName = node.qualifier()?.value() || '';

        if (qualName) {
            const table = compositeRow[qualName];
            if (table === TBL_PLACEHOLDER) {
                return node.clone();
            }
            if (!table) throw new Error(`Table alias ${qualName} not found in the current context`);
            return table[colName];
        }
        for (const alias of Object.keys(compositeRow)) {
            const table = compositeRow[alias];
            let _colName = colName;
            
            if (_colName in table) return table[_colName];
            if ((_colName = colName.toLowerCase()) in table) return table[_colName];
            if ((_colName = colName.toUpperCase()) in table) return table[_colName];
        }

        throw new Error(`Column ${colName} not found in the current context`);
    }

    async BIND_VAR(node, compositeRow, queryCtx = {}) {
        if (!Array.isArray(queryCtx.options.values))
            throw new Error(`there is no parameter ${node}`);
        const value = Number(node.value());
        if (queryCtx.options.values.length < value)
            throw new Error(`there is no parameter ${node}`);
        return queryCtx.options.values[value - 1];
    }

    async DEFAULT_LITERAL(node) { return null; }
    async STRING_LITERAL(node) { return node.value(); }
    async NUMBER_LITERAL(node) { return Number(node.value()); }
    async BOOL_LITERAL(node) { return node.value() === 'TRUE'; }
    async NULL_LITERAL() { return null; }

    // -------------

    resolveScopedRefsInClause(clause, selectList) {
        return clause.entries().map((entry) => {
            let refedExpr;
            if (entry.expr() instanceof registry.NumberLiteral) {
                if (!(refedExpr = selectList.entries()[parseInt(entry.expr().value()) - 1]?.expr())) {
                    throw new Error(`[${clause}] The reference by offset ${entry.expr().value()} does not resolve to a select list entry`);
                }
            } else if (entry.expr()?.resolution?.() === 'scope') {
                refedExpr = selectList.entries().find((si, i) => si.alias()?.identifiesAs(entry.expr()))?.expr();
            }
            if (refedExpr) {
                entry = entry.constructor.fromJSON({ ...entry.jsonfy(), expr: refedExpr.jsonfy() }, { assert: true });
                clause._adoptNodes(entry);
            }
            return entry;
        });
    }

    applySorting(decorated, orderElements, queryCtx = {}) {
        // Sort synchronously
        decorated.sort((a, b) => {
            for (let i = 0; i < orderElements.length; i++) {
                const idDesc = orderElements[i].dir() === 'DESC';
                const dir = idDesc ? -1 : 1;
                const nullsSpec = orderElements[i].nullsSpec()
                    || (queryCtx.options?.dialect === 'mysql' ? (idDesc ? 'LAST' : 'FIRST') : (idDesc ? 'FIRST' : 'LAST'));

                const valA = a.keys[i];
                const valB = b.keys[i];
                const aIsNull = valA === null; // Explicit NULL check
                const bIsNull = valB === null;

                // 1. Handle NULL vs. NULL (Always equal)
                if (aIsNull && bIsNull) continue; // Move to next order element

                // 2. Handle NULL vs. Non-NULL
                if (aIsNull || bIsNull) {
                    // Determine the NULLs order required by the SQL dialect/spec
                    // If NULLS FIRST:
                    //   - A is NULL, B is NOT: A comes first (return -1)
                    //   - B is NULL, A is NOT: B comes first (return 1)
                    if (nullsSpec === 'FIRST') {
                        if (aIsNull) return -1; // A comes first
                        if (bIsNull) return 1;  // A comes after B
                    }
                    // If NULLS LAST:
                    //   - A is NULL, B is NOT: A comes last (return 1)
                    //   - B is NULL, A is NOT: B comes last (return -1)
                    else { // NULLS LAST
                        if (aIsNull) return 1;  // A comes after B
                        if (bIsNull) return -1; // A comes before B
                    }
                }

                // 3. Handle Non-NULL vs. Non-NULL (Original logic)
                // Ensure comparison is safe for potentially non-numeric/string values if needed
                if (valA < valB) return -dir;
                if (valA > valB) return dir;
            }
            return 0;
        });
    }
}

function toNode(x) {
    if (x === undefined
        || x instanceof AbstractNode) return x;
    if (typeof x === 'number') return registry.NumberLiteral.fromJSON({ value: x });
    if (typeof x === 'string') return registry.StringLiteral.fromJSON({ value: x });
    if (typeof x === 'boolean') return registry.BoolLiteral.fromJSON({ value: x });
    if (x === null) return registry.NullLiteral.fromJSON({ value: x });
    throw new Error(`Cannot convert value to AST node: ${x}`);
}

function likeCompare(str, pattern) {
    const esc = pattern
        .replace(/([.+^=!:${}()|\[\]\\/])/g, '\\$1')
        .replace(/%/g, '.*')
        .replace(/_/g, '.');
    try {
        return new RegExp(`^${esc}$`, 'i').test(str);
    } catch {
        return false;
    }
}
